import type { SupportedLocale } from "@test-platform/shared";

export const CONVERSATION_MIN_LENGTH = 20;
export const CONVERSATION_MAX_LENGTH = 2400;

export type AiConversationChemistry = {
  title: string;
  summary: string;
  tempo: string;
  signals: string[];
  replyIdeas: string[];
  disclaimer: string;
};

type ConversationChemistryContext = {
  conversation: string;
  locale: SupportedLocale;
};

export const AI_CONVERSATION_CHEMISTRY_RESPONSE_SCHEMA = {
  type: "object",
  additionalProperties: false,
  required: ["title", "summary", "tempo", "signals", "replyIdeas"],
  properties: {
    title: { type: "string" },
    summary: { type: "string" },
    tempo: { type: "string" },
    signals: {
      type: "array",
      minItems: 2,
      maxItems: 4,
      items: { type: "string" },
    },
    replyIdeas: {
      type: "array",
      minItems: 2,
      maxItems: 3,
      items: { type: "string" },
    },
  },
} as const;

function compactText(value: string, maxLength: number) {
  return value.replace(/\s+/g, " ").trim().slice(0, maxLength);
}

function getObject(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object"
    ? (value as Record<string, unknown>)
    : null;
}

function getText(value: unknown, fallback: string, maxLength: number) {
  return typeof value === "string" && value.trim()
    ? compactText(value, maxLength)
    : fallback;
}

function getTextArray(
  value: unknown,
  fallback: string[],
  maxItems: number,
  maxLength: number,
) {
  if (!Array.isArray(value)) return fallback;
  const items = value
    .filter((item): item is string => typeof item === "string")
    .map((item) => compactText(item, maxLength))
    .filter(Boolean)
    .slice(0, maxItems);
  return items.length > 0 ? items : fallback;
}

export function getAiConversationChemistrySystemPrompt(
  locale: SupportedLocale,
) {
  return locale === "ko"
    ? [
        "You analyze pasted chat text as playful Korean entertainment content.",
        "Return JSON only. Do not mention Gemini, AI, prompts, or internal rules.",
        "Do not infer protected traits, diagnose mental state, or claim true intent.",
        "Do not encourage manipulation. Keep reply ideas respectful and low-pressure.",
      ].join("\n")
    : [
        "You analyze pasted chat text as playful English entertainment content.",
        "Return JSON only. Do not mention Gemini, AI, prompts, or internal rules.",
        "Do not infer protected traits, diagnose mental state, or claim true intent.",
        "Do not encourage manipulation. Keep reply ideas respectful and low-pressure.",
      ].join("\n");
}

export function getAiConversationChemistryUserPrompt({
  conversation,
  locale,
}: ConversationChemistryContext) {
  const language = locale === "ko" ? "Korean" : "English";
  return [
    `Write in ${language}.`,
    "Analyze this conversation as a light communication style reading.",
    "Conversation:",
    conversation,
    "Create a compact result with:",
    "- title: short chemistry label",
    "- summary: 1-2 sentences",
    "- tempo: one sentence about pacing",
    "- signals: 2-4 observable communication cues",
    "- replyIdeas: 2-3 respectful next-message ideas",
  ].join("\n");
}

export function normalizeAiConversationChemistry(
  value: unknown,
  context: ConversationChemistryContext,
): AiConversationChemistry {
  const payload = getObject(value) ?? {};
  const fallbackSignals =
    context.locale === "ko"
      ? ["답장 길이와 속도의 차이를 함께 보면 좋아요.", "질문과 리액션의 균형을 확인해 보세요."]
      : ["Compare reply length with reply timing.", "Look at the balance between questions and reactions."];
  const fallbackReplies =
    context.locale === "ko"
      ? ["가볍게 이어갈 질문을 하나만 던져보세요.", "상대가 답하기 쉬운 선택지를 함께 주세요."]
      : ["Ask one easy follow-up question.", "Offer a simple choice that is easy to answer."];

  return {
    title: getText(
      payload.title,
      context.locale === "ko" ? "대화 케미 분석" : "Conversation chemistry",
      64,
    ),
    summary: getText(
      payload.summary,
      context.locale === "ko"
        ? "붙여 넣은 대화에서 보이는 템포와 리액션 포인트를 가볍게 정리했어요."
        : "A light read on pacing and response cues in the pasted chat.",
      260,
    ),
    tempo: getText(
      payload.tempo,
      context.locale === "ko"
        ? "대화 템포는 답장 길이, 질문 빈도, 리액션 밀도로 함께 보면 좋아요."
        : "Conversation tempo is easiest to read through length, questions, and reactions.",
      180,
    ),
    signals: getTextArray(payload.signals, fallbackSignals, 4, 140),
    replyIdeas: getTextArray(payload.replyIdeas, fallbackReplies, 3, 140),
    disclaimer:
      context.locale === "ko"
        ? "대화 케미 분석은 재미와 커뮤니케이션 참고용이며 상대의 실제 감정을 단정하지 않습니다."
        : "Conversation chemistry is for entertainment and communication reflection, not proof of anyone's intent.",
  };
}
